import React, { useId, useState } from "react";
import { Link } from "react-router-dom";

import { downloadAnalyticsCsv } from "../../api/analyticsApi";
import { Btn, Icon, Notice, Spinner, Thumb, cx } from "../catalogProductStudio/edit/StudioUi";
import {
  describeChange,
  fill,
  formatChange,
  formatMoney,
  formatNumber,
  formatPercent,
  isMissing,
} from "./analyticsModel";

// Presentation primitives for Analytics. Classes are prefixed `an-` and styled
// in assets/css/analytics.css on top of the Product Studio `.pse` tokens.

/** Accessible "how is this calculated" tooltip (hover and keyboard focus). */
export const InfoTip = ({ text, label }) => {
  const id = useId();
  if (!text) return null;
  return (
    <span className="an-tip">
      <button type="button" className="an-tip__trigger" aria-describedby={id} aria-label={label}>
        <Icon name="info" size={14} />
      </button>
      <span role="tooltip" id={id} className="an-tip__bubble">
        {text}
      </span>
    </span>
  );
};

export const ChangeBadge = ({ change, invert, lang, tt }) => {
  const model = describeChange(change, { invert });
  if (model.direction === "none") {
    return <span className="an-change is-none">{tt("noPrevious")}</span>;
  }
  return (
    <span className={cx("an-change", `is-${model.tone}`)}>
      {model.direction === "up" ? "▲" : model.direction === "down" ? "▼" : "■"} {formatChange(change, lang)}
    </span>
  );
};

/**
 * KPI tile. `value` null means "not available" (missing data), which is shown
 * differently from a real zero. `restricted` means the viewer may not see it.
 */
export const KpiCard = ({ label, help, display, compared, value, invert, sub, restricted, tt, lang, tone, badge }) => {
  const raw = value !== undefined ? value : compared?.value;
  const unavailable = !restricted && (value !== undefined || Boolean(compared)) && isMissing(raw);
  return (
    <div className={cx("an-kpi", tone && `an-kpi--${tone}`)}>
      <div className="an-kpi__label">
        <span>{label}</span>
        <InfoTip text={help} label={tt("howCalculated")} />
        {badge ? <span className="an-kpi__badge">{badge}</span> : null}
      </div>
      {restricted ? (
        <div className="an-kpi__value is-muted">
          <Icon name="lock" size={16} /> <span className="an-kpi__unavailable">{tt("costHidden")}</span>
        </div>
      ) : unavailable ? (
        <div className="an-kpi__value is-muted">
          <span className="an-kpi__unavailable">{tt("unavailable")}</span>
        </div>
      ) : (
        <div className="an-kpi__value">{display}</div>
      )}
      {!restricted && compared ? (
        <div className="an-kpi__meta">
          <ChangeBadge change={compared.change} invert={invert} lang={lang} tt={tt} />
          {sub ? <span className="an-kpi__sub">{sub}</span> : null}
        </div>
      ) : sub ? (
        <div className="an-kpi__meta">
          <span className="an-kpi__sub">{sub}</span>
        </div>
      ) : null}
    </div>
  );
};

export const Panel = ({ title, hint, actions, children, className, id }) => (
  <section className={cx("pse-card an-panel", className)} aria-labelledby={id ? `${id}-title` : undefined}>
    {title || actions ? (
      <header className="an-panel__head">
        <div>
          {title ? (
            <h3 className="an-panel__title" id={id ? `${id}-title` : undefined}>
              {title}
            </h3>
          ) : null}
          {hint ? <p className="an-panel__hint">{hint}</p> : null}
        </div>
        {actions ? <div className="an-panel__actions">{actions}</div> : null}
      </header>
    ) : null}
    <div className="an-panel__body">{children}</div>
  </section>
);

/** Loading / error / empty wrapper so every section handles them the same way. */
export const DataState = ({ query, tt, isEmpty, emptyTitle, emptyBody, children }) => {
  if (query.isLoading || (query.isFetching && !query.data)) {
    return (
      <div className="an-state" aria-busy="true">
        <Spinner label={tt("loading")} />
        <div className="an-skeleton" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </div>
    );
  }
  if (query.isError) {
    const status = query.error?.status;
    if (status === 403) {
      return (
        <Notice tone="warning" icon="lock" title={tt("restrictedTitle")}>
          {tt("restrictedBody")}
        </Notice>
      );
    }
    return (
      <Notice
        tone="danger"
        title={tt("loadError")}
        action={
          <Btn size="sm" icon="refresh" onClick={() => query.refetch()}>
            {tt("retry")}
          </Btn>
        }
      >
        {query.error?.data?.message ? String(query.error.data.message) : null}
      </Notice>
    );
  }
  if (!query.data) return null;
  if (isEmpty) {
    return (
      <div className="an-empty">
        <Icon name="cart" size={28} />
        <strong>{emptyTitle || tt("emptyTitle")}</strong>
        <span>{emptyBody || tt("emptyBody")}</span>
      </div>
    );
  }
  return <div className={cx("an-state-ready", query.isFetching && "is-refreshing")}>{children}</div>;
};

/**
 * Horizontal bar list (magnitude by category). Labels and values are text in
 * ink colours; the bar only carries magnitude.
 */
export const BarList = ({ rows, valueKey = "value", format, secondary, emptyText, tone = "1", labelOf }) => {
  const max = Math.max(0, ...rows.map((row) => Math.abs(Number(row[valueKey]) || 0)));
  if (!rows.length) return <p className="an-muted">{emptyText}</p>;
  return (
    <ul className="an-barlist">
      {rows.map((row) => {
        const value = Number(row[valueKey]) || 0;
        const width = max > 0 && value !== 0 ? Math.max(2, (Math.abs(value) / max) * 100) : 0;
        return (
          <li key={row.key} className="an-barlist__row">
            <div className="an-barlist__text">
              <span className="an-barlist__label">{labelOf ? labelOf(row) : row.label}</span>
              <span className="an-barlist__value">{format(value, row)}</span>
            </div>
            <div className="an-barlist__track" aria-hidden="true">
              <span className={cx("an-barlist__bar", `is-tone-${tone}`, value < 0 && "is-negative")} style={{ width: `${width}%` }} />
            </div>
            {secondary ? <div className="an-barlist__secondary">{secondary(row)}</div> : null}
          </li>
        );
      })}
    </ul>
  );
};

/** Sortable table. `columns[i].sortKey` makes a header clickable. */
export const DataTable = ({ columns, rows, rowKey, sort, direction, onSort, caption, emptyText, dense }) => (
  <div className="an-table-wrap">
    <table className={cx("an-table", dense && "is-dense")}>
      {caption ? <caption className="pc-sr-only">{caption}</caption> : null}
      <thead>
        <tr>
          {columns.map((column) => {
            const active = column.sortKey && sort === column.sortKey;
            return (
              <th
                key={column.key}
                scope="col"
                className={cx(column.align === "end" && "is-end", column.className)}
                aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : undefined}
              >
                {column.sortKey && onSort ? (
                  <button type="button" className={cx("an-sort", active && "is-active")} onClick={() => onSort(column.sortKey)}>
                    {column.label}
                    <span aria-hidden="true">{active ? (direction === "asc" ? " ↑" : " ↓") : " ↕"}</span>
                  </button>
                ) : (
                  column.label
                )}
                {column.help ? <InfoTip text={column.help} label={column.label} /> : null}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.length ? (
          rows.map((row, index) => (
            <tr key={rowKey ? rowKey(row) : index}>
              {columns.map((column) => (
                <td key={column.key} className={cx(column.align === "end" && "is-end", column.className)}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={columns.length} className="an-table__empty">
              {emptyText}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export const Pager = ({ pagination, onPage, tt }) => {
  if (!pagination || pagination.totalPages <= 1) {
    return pagination ? <p className="an-pager__count">{fill(tt("rowsCount"), { count: pagination.total })}</p> : null;
  }
  return (
    <nav className="an-pager" aria-label={tt("page")}>
      <span className="an-pager__count">{fill(tt("rowsCount"), { count: pagination.total })}</span>
      <Btn size="sm" icon="chevronLeft" disabled={pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}>
        {tt("previous")}
      </Btn>
      <span className="an-pager__label">{fill(tt("page"), { page: pagination.page, total: pagination.totalPages })}</span>
      <Btn size="sm" iconRight="chevronRight" disabled={pagination.page >= pagination.totalPages} onClick={() => onPage(pagination.page + 1)}>
        {tt("next")}
      </Btn>
    </nav>
  );
};

export const ExportButton = ({ dataset, params, tt, label, size = "sm" }) => {
  const [state, setState] = useState("idle");
  const run = async () => {
    setState("busy");
    try {
      await downloadAnalyticsCsv(dataset, params);
      setState("idle");
    } catch {
      setState("error");
    }
  };
  return (
    <span className="an-export">
      <Btn size={size} icon="download" onClick={run} disabled={state === "busy"}>
        {state === "busy" ? tt("exporting") : label || tt("exportCsv")}
      </Btn>
      {state === "error" ? (
        <span className="an-export__error" role="alert">
          {tt("exportError")}
        </span>
      ) : null}
    </span>
  );
};

/** Server data-quality notes, rendered with their numbers. */
export const DataNotes = ({ notes, tt, lang }) => {
  if (!notes?.length) return null;
  return (
    <div className="an-notes">
      {notes.map((note) => {
        const params = note.params || {};
        const text = fill(tt(`note_${note.code}`), {
          ...params,
          coverage: formatPercent(params.coverage, lang),
          estimated: params.recordedCoverage === undefined ? "" : formatPercent(1 - (params.recordedCoverage ?? 0), lang),
          amount: formatMoney(params.amount ?? params.value ?? params.uncoveredRevenue ?? params.uncoveredRefunds, lang),
        });
        return (
          <p key={note.code} className={cx("an-note", `is-${note.severity}`)}>
            <Icon name={note.severity === "warning" ? "alert" : "info"} size={15} />
            <span>{text}</span>
          </p>
        );
      })}
    </div>
  );
};

/** Thumbnail + title linking to the product in Product Studio. */
export const ProductCell = ({ productId, imageUrl, title, sub, tt }) => (
  <div className="an-product">
    <Thumb src={imageUrl} size={36} fallbackLabel={title} />
    <div className="an-product__text">
      {productId ? (
        <Link to={`/admin/catalog/products/${productId}`} className="an-product__title" title={tt("openProduct")}>
          {title}
        </Link>
      ) : (
        <span className="an-product__title">{title}</span>
      )}
      {sub ? <span className="an-product__sub">{sub}</span> : null}
    </div>
  </div>
);

export const Money = ({ value, lang }) => <span className={cx(isMissing(value) && "an-muted")}>{formatMoney(value, lang)}</span>;
export const Num = ({ value, lang, digits }) => <span className={cx(isMissing(value) && "an-muted")}>{formatNumber(value, lang, digits)}</span>;
export const Pct = ({ value, lang }) => <span className={cx(isMissing(value) && "an-muted")}>{formatPercent(value, lang)}</span>;
